import { Alert, Button, Divider, InputField } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import { useTranslation } from 'next-i18next';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  LazyCloudflareTurnstile,
  useResetTurnstile,
} from '../../../components/Captcha/LazyCloudflareTurnstile';
import StickyHeader from '../../../components/Containers/StickyHeader';
import { Image } from '../../../components/Image';
import { ArticleBackground } from '../../../components/Journals/ArticleBackground';
import Layout from '../../../components/Layout';
import PageSection from '../../../components/Layout/PageSection';
import {
  HorizontalBarChart,
  HorizontalLineChart,
  PieChart,
  VerticalBarChart,
  WorldMapSVG,
} from '../../../components/Reports';
import ScrollAnchor from '../../../components/ScrollAnchor';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Franchises, HeroImage } from '../../../types/Franchises';
import {
  GeneratedHubspotLists,
  useHubspotListMutation,
} from '../../../types/generated';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';
import contentJSON from './collector-report-2024.json';

const scrollToShortcut = (ref: React.RefObject<HTMLElement>) => {
  ref?.current?.scrollIntoView({
    behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
  });
};

type ContentProps = {
  id: string;
  items?: string[];
  component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'ul' | 'ol';
  content: string;
};

const parseMarkDownToHtml = (mdString: string): JSX.Element => {
  const parts = mdString.split(/(\[.*?\]\(.*?\))/);

  return (
    <>
      {parts.map((part) => {
        if (part.startsWith('[') && part.endsWith(')')) {
          const text = part.substring(1, part.indexOf(']'));
          const url = part.substring(part.indexOf('(') + 1, part.length - 1);

          return (
            <a href={url} rel="noreferrer" target="_blank">
              {text}
            </a>
          );
        }
        return <>{part}</>;
      })}
    </>
  );
};

const renderContent = (el: ContentProps) => {
  switch (el.component) {
    case 'h3':
      return (
        <h3 className="w-full text-white">{parseMarkDownToHtml(el.content)}</h3>
      );
    case 'p':
      return (
        <p className="w-full text-white">{parseMarkDownToHtml(el.content)}</p>
      );
    case 'ol':
      if (!el.items || el.items.length === 0) return null;
      return (
        <ol className="!mt-0 w-full text-left text-white">
          {el.items.map((li: string) => (
            <li>{parseMarkDownToHtml(li)}</li>
          ))}
        </ol>
      );
    case 'ul':
      if (!el.items || el.items.length === 0) return null;
      return (
        <ul className="!mt-0 w-full text-left text-white">
          {el.items.map((li: string) => (
            <li>{parseMarkDownToHtml(li)}</li>
          ))}
        </ul>
      );
    default:
      return null;
  }
};

type PillProps = {
  icon: string;
  label: string;
};

const Pill = ({ icon, label }: PillProps) => (
  <div className="flex space-x-2 rounded-full bg-neutral-6/[0.01] px-3 py-2 ring-1 ring-neutral-4/20 backdrop-blur-2xl">
    <span className="text-center text-[20px] leading-none">{icon}</span>
    <span className="text-center">{label}</span>
  </div>
);

type InformationProps = {
  index?: number;
  title: string;
  content: ContentProps[];
  overline?: string;
  isCenterAligned?: boolean;
};

type ColumnInformationProps = {
  component?: 'ColumnInformation';
  index?: number;
  title: string;
  content: ContentProps[];
  overline?: string;
  isCenterAligned?: boolean;
};

const ColumnInformation = ({
  component,
  content,
  index,
  isCenterAligned,
  overline,
  title,
}: ColumnInformationProps) => (
  <div
    className={classNames(
      isCenterAligned ? 'items-center' : 'items-center md:items-start',
      'flex flex-col space-y-6',
    )}
    data-component={component}
  >
    {index && (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-6/[0.01] text-center ring-1 ring-divider/20 backdrop-blur-2xl">
        {index}
      </span>
    )}
    <div className="flex flex-col space-y-3 text-left">
      {overline && (
        <p
          className={classNames(
            isCenterAligned ? 'text-center' : 'text-center md:text-left',
            'leading-normal text-neutral-3',
          )}
        >
          {overline}
        </p>
      )}
      <h3
        className={classNames(
          isCenterAligned ? 'text-center' : 'text-center md:text-left',
          'text-balance text-lg font-bold leading-tight tracking-tight',
        )}
      >
        {title}
      </h3>
    </div>
    <div className="aa-prose flex flex-col space-y-3 text-left !leading-normal">
      {content.map((paragraph) => (
        <div key={paragraph.id}>{renderContent(paragraph)}</div>
      ))}
    </div>
  </div>
);

type GridProps = {
  index?: number;
  title: string;
  content: ContentProps[];
};

type ColumnIntroProps = {
  component?: 'ColumnIntro';
  title: string;
  content: ContentProps[];
};

const ColumnIntro = ({ component, content, title }: ColumnIntroProps) => (
  <div
    className="flex flex-col items-center space-y-6"
    data-component={component}
  >
    {title && (
      <h3 className="mx-auto w-full max-w-md text-center text-lg font-bold leading-tight tracking-tight text-white">
        {title}
      </h3>
    )}

    <div className="aa-prose flex flex-col space-y-3 text-left !leading-normal">
      {content.map((paragraph) => (
        <div key={paragraph.id}>{renderContent(paragraph)}</div>
      ))}
    </div>
  </div>
);

type ChartData = {
  _id: string;
  value: number;
  suffix?: string;
  color: '#7F200F' | '#FF5207' | '#FFCEA8' | '#FFFFFF' | '#BFD7FE' | '#2570EB';
};

type ChartProps = {
  dataset: {
    id: string;
    label: string;
    data: ChartData[];
  }[];
  maxValue?: number;
};

type ColumnChartProps = {
  component: 'HorizontalLineChart' | 'HorizontalBarChart' | 'VerticalBarChart';
  dataset: {
    id: string;
    label: string;
    data: ChartData[];
  }[];
  title?: string;
  maxValue?: number;
};

const renderChart = (component: string, props: ChartProps) => {
  switch (component) {
    case 'HorizontalLineChart':
      return <HorizontalLineChart {...props} />;
    case 'HorizontalBarChart':
      return <HorizontalBarChart {...props} />;
    case 'VerticalBarChart':
      return <VerticalBarChart {...props} />;
    case 'PieChart':
      return <PieChart {...props} />;
    default:
      return null;
  }
};

const ColumnChart = ({
  component,
  dataset,
  maxValue,
  title,
}: ColumnChartProps) => (
  <div
    className="flex flex-col justify-end space-y-6 rounded-lg bg-neutral-5/30 p-6 ring-1 ring-neutral-4/20 backdrop-blur-2xl md:space-y-9 md:p-9"
    data-component={component}
  >
    {title && (
      <h4 className="mx-auto w-full max-w-xs text-balance text-center font-bold leading-normal">
        {title}
      </h4>
    )}

    {renderChart(component, { dataset, maxValue: maxValue || 100 })}
  </div>
);

type SectionHeroProps = {
  component?: 'SectionHero';
  overline: string;
  title: string;
  content: ContentProps[];
  destinationRef: RefObject<HTMLAnchorElement>;
  className?: string;
};

const SectionHero = ({
  className,
  component,
  content,
  destinationRef,
  overline,
  title,
}: SectionHeroProps) => {
  const handleScrollTo = useCallback(() => {
    scrollToShortcut(destinationRef);
  }, [destinationRef]);

  return (
    <PageSection className={className}>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          y: 0,
          transition: {
            type: 'easeOut',
            duration: 0.8,
            delay: 0.4,
          },
        }}
      >
        <div
          className="flex flex-col items-center justify-center space-y-6 py-12 md:space-y-12 md:py-36 lg:py-48"
          data-component={component}
        >
          <h1 className="mx-auto w-full max-w-5xl text-balance text-center text-[2rem] font-bold leading-[1.125] tracking-tight md:text-[3rem] md:tracking-tight lg:text-[4rem] lg:-tracking-[0.0375em]">
            {title}
          </h1>

          <div className="aa-prose mx-auto flex w-full max-w-md flex-col space-y-3 text-center !leading-normal">
            {content.map((paragraph) => (
              <div key={paragraph.id}>{renderContent(paragraph)}</div>
            ))}
          </div>

          <Button
            label="Download Full Report"
            onClick={handleScrollTo}
            size="lg"
            variant="primary"
          />

          <p className="mx-auto w-full max-w-md text-center leading-normal text-neutral-3">
            {overline}
          </p>
        </div>
      </motion.div>
    </PageSection>
  );
};

type SectionHeaderProps = {
  component?: 'SectionHeader';
  overline: string;
  title: string;
  className?: string;
  content?: ContentProps[];
};

const SectionHeader = ({
  className,
  component,
  content,
  overline,
  title,
}: SectionHeaderProps) => (
  <PageSection className={className}>
    <div
      className="flex flex-col space-y-6 py-12 md:py-36 lg:py-48"
      data-component={component}
    >
      <div className="flex flex-col space-y-2">
        <p className="mx-auto w-full max-w-md text-center leading-normal text-neutral-3">
          {overline}
        </p>
        <h2 className="mx-auto w-full max-w-3xl text-balance text-center text-[2rem] font-bold leading-[1.125] tracking-tight md:text-[3rem] md:tracking-tight lg:text-[4rem] lg:-tracking-[0.0375em]">
          {title}
        </h2>
      </div>

      {content && (
        <div className="aa-prose mx-auto flex w-full max-w-md flex-col space-y-3 text-left !leading-normal md:text-center">
          {content.map((paragraph) => (
            <div key={paragraph.id}>{renderContent(paragraph)}</div>
          ))}
        </div>
      )}
    </div>
  </PageSection>
);

type SectionMapProps = {
  component?: 'SectionMap';
  countries: PillProps[];
  className?: string;
};

const SectionMap = ({ className, component, countries }: SectionMapProps) => (
  <PageSection className={className}>
    <div
      className="flex w-full flex-col items-center justify-center"
      data-component={component}
    >
      <WorldMapSVG className="h-full w-full transition duration-300 ease-out" />

      <div className="relative z-20 mx-auto -mt-3 flex w-full max-w-lg flex-col items-center justify-center space-y-3 md:-mt-12 lg:-mt-20">
        <p className="text-center font-bold leading-normal">
          Top Collector Geographies
        </p>
        <div className="flex w-full flex-wrap items-center justify-center gap-2 md:gap-3">
          {countries.map(({ icon, label }) => Pill({ icon, label }))}
        </div>
      </div>
    </div>
  </PageSection>
);

type SectionMediaProps = {
  component?: 'SectionMedia';
  className?: string;
  images: { image: StoryblokImage; ratio: '16/9' | '4/3' | '3/4' }[];
};

const SectionMedia = ({ className, component, images }: SectionMediaProps) => (
  <PageSection className={className}>
    <motion.div
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{
        opacity: 1,
        transition: {
          type: 'easeOut',
          duration: 0.8,
        },
      }}
    >
      <div
        className="flex flex-col gap-6 md:flex-row md:gap-3"
        data-component={component}
      >
        {images.map(({ image, ratio }) => (
          <figure className="flex w-full flex-col space-y-3">
            <div
              className="flex aspect-[var(--ratio)] w-full items-center justify-center overflow-hidden rounded-sm text-center"
              style={{ '--ratio': ratio } as React.CSSProperties}
            >
              <Image
                alt={image.alt}
                className="h-full"
                focus={image.focus}
                size={{
                  sm: { ratio, span: 12 },
                  md: { ratio, span: 6 },
                  lg: { ratio, span: 6 },
                }}
                src={image.filename}
              />
            </div>
            {image.title && (
              <figcaption className="max-w-md text-balance text-xs text-neutral-3">
                {image.title}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </motion.div>
  </PageSection>
);

type SectionSubscriptionProps = {
  component?: 'SectionSubscription';
  image: StoryblokImage;
  title: string;
  className?: string;
};

const SectionSubscription = ({
  className,
  component,
  image,
  title,
}: SectionSubscriptionProps) => {
  const { t } = useTranslation(['reports']);

  const [captchaToken, setCaptchaToken] = useState('');
  const { turnstileRef, turnstileReset } = useResetTurnstile();
  const [hubspotListMutation] = useHubspotListMutation();

  const form = useForm<{
    email: string;
  }>({
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const { isValid } = form.formState;
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await hubspotListMutation({
        variables: {
          email: form.getValues().email,
          captcha: captchaToken,
          list: GeneratedHubspotLists.Whitepaper2024,
        },
      });
      setSuccess(true);
      form.reset();
    } catch (err) {
      setSuccess(false);
      turnstileReset();
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hubspotListMutation, captchaToken, form, turnstileReset]);

  return (
    <PageSection className={className}>
      <div
        className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-md bg-transparent"
        data-component={component}
      >
        <div
          className="absolute bottom-0 left-0 right-0 top-0 z-0 contrast-[1.2]"
          style={{
            'backgroundImage': `url(${image.filename})`,
            'backgroundPosition': 'center center',
            'backgroundSize': 'cover',
            'backgroundRepeat': 'no-repeat',
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 top-0 z-10 bg-black/50" />

        <div className="z-20 flex w-full flex-col items-center px-6 py-12 md:py-36">
          <h3 className="mx-auto w-full max-w-2xl text-balance text-center text-[2rem] font-bold leading-[1.125] tracking-tight md:text-[3rem] md:tracking-tight lg:text-[4rem] lg:-tracking-[0.0375em]">
            {title}
          </h3>

          <div className="mt-12 flex w-full max-w-lg flex-col space-y-3">
            {isSuccess && (
              <Alert status="success">
                {t('reports:subscription.alert.success')}
              </Alert>
            )}

            <div className="flex w-full flex-col gap-3 md:flex-row">
              <InputField
                id="email"
                placeholder={t('reports:subscription.email.placeholder')}
                type="email"
                {...form.register('email', {
                  required: true,
                  pattern: /\S+@\S+\.\S+/,
                })}
              />
              <div className="!w-full shrink-0 md:!w-auto">
                <Button
                  disabled={!isValid || isLoading}
                  fullWidth
                  label={t('reports:subscription.ctaLabel.default')}
                  loading={isLoading}
                  onClick={() => void handleSubmit()}
                  size="md"
                  variant="primary"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 w-full max-w-lg">
            <LazyCloudflareTurnstile
              actionName="collector_report_subscribe"
              onError={() => setCaptchaToken('')}
              onExpire={() => setCaptchaToken('')}
              onSuccess={setCaptchaToken}
              turnstileRef={turnstileRef}
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

type SectionColumnsProps = {
  component?: 'SectionColumns';
  chart?: ColumnChartProps;
  information: ColumnInformationProps | ColumnIntroProps;
  isReverse?: boolean;
  className?: string;
};

const SectionColumns = ({
  chart,
  className,
  component,
  information,
  isReverse = false,
}: SectionColumnsProps) => (
  <PageSection className={className}>
    <div
      className="flex flex-col items-center justify-center gap-6 py-12 md:flex-row md:gap-12 md:py-36 lg:py-48"
      data-component={component}
    >
      <div
        className={classNames({ 'md:order-last': isReverse }, 'w-full flex-1')}
      >
        <div className="mx-auto w-full max-w-md">
          {information.component === 'ColumnIntro' ? (
            <ColumnIntro {...(information as GridProps)} />
          ) : (
            <ColumnInformation
              {...(information as InformationProps)}
              isCenterAligned={!chart}
            />
          )}
        </div>
      </div>
      {chart && (
        <div className="w-full flex-1">
          <div className="mx-auto w-full max-w-md">
            <ColumnChart {...chart} />
          </div>
        </div>
      )}
    </div>
  </PageSection>
);

type DividerProps = {
  component?: 'Divider';
};

type SectionComponentProps =
  | SectionHeroProps
  | SectionHeaderProps
  | SectionMapProps
  | SectionMediaProps
  | SectionSubscriptionProps
  | SectionColumnsProps
  | DividerProps;

type PageProps = {
  locale: string;
  locales: string[];
  ogImageUrl: string;
  preview: boolean;
  story: Franchises;
  backgroundImage: StoryblokImage;
};

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(context.locale || 'en', [
    'reports',
  ]);

  const page: StoryResponse<Franchises> = await getStoryblokPage(
    'cdn/stories/insights/articles/collector-report-2024',
    {
      locale: context.locale,
      preview: context.preview,
    },
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  const story = page.data?.story;
  const heroPicker = story.content.heroPicker[0] as HeroImage;
  const backgroundImage = heroPicker.image;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: story.content.title,
    imageSrc: backgroundImage.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
  });

  return {
    props: {
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl,
      story,
      backgroundImage,
      ...translations,
    },
  };
}

const Page = ({
  backgroundImage,
  locale,
  locales,
  ogImageUrl,
  preview,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const page = useStoryblok(story, preview, locale);

  const { t } = useTranslation(['reports']);
  const isMobile = useMediaQuery('sm');

  const ctaRef = useRef<HTMLAnchorElement>(null);
  const SectionSubscriptionRef = useRef<HTMLAnchorElement>(null);
  const handleScrollTo = useCallback(() => {
    scrollToShortcut(SectionSubscriptionRef);
  }, [SectionSubscriptionRef]);

  const sections = contentJSON as SectionComponentProps[];

  const [toastIsOpened, setToastIsOpened] = useState(false);

  useEffect(() => {
    if (toastIsOpened) {
      setTimeout(() => {
        setToastIsOpened(false);
      }, 5000);
    }
  }, [toastIsOpened]);

  return (
    <Layout
      description={page.content.seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      theme="dark"
      title={page.content.seo.title}
    >
      <StickyHeader ctaRef={ctaRef} theme="dark">
        <div className="flex w-full items-center space-x-3">
          <div className="flex w-full flex-col overflow-hidden">
            <p className="line-clamp-2 text-balance text-neutral-3">
              {page.content.title}
            </p>
          </div>
          <div className="shrink-0">
            <Button
              label={t('reports:cta.default')}
              onClick={handleScrollTo}
              size={isMobile ? 'sm' : 'md'}
              variant="primary"
            />
          </div>
        </div>
      </StickyHeader>

      <ArticleBackground background={backgroundImage} className="fixed" />

      <div className="flex w-full flex-col space-y-9 py-9 text-white md:space-y-12 md:py-12">
        {sections.map((section: SectionComponentProps) => {
          switch (section.component) {
            case 'SectionHero':
              return (
                <div>
                  <ScrollAnchor ref={ctaRef} id="heroRef" />
                  <SectionHero
                    {...section}
                    destinationRef={SectionSubscriptionRef}
                  />
                </div>
              );
            case 'SectionHeader':
              return <SectionHeader {...section} />;
            case 'SectionMap':
              return <SectionMap {...section} />;
            case 'SectionColumns':
              return <SectionColumns {...section} />;
            case 'SectionMedia':
              return <SectionMedia {...section} />;
            case 'SectionSubscription':
              return (
                <div>
                  <ScrollAnchor
                    ref={SectionSubscriptionRef}
                    id="SectionSubscription"
                    offset={-72}
                  />
                  <SectionSubscription {...section} />
                </div>
              );
            case 'Divider':
              return <Divider />;
            default:
              return null;
          }
        })}
      </div>
    </Layout>
  );
};

export default Page;
